<template>
  <div>
    <div class="payment-charge-wrap form-area" :inert="modalOpen">
      <payment-form @paymentResponse="paymentResponse" @validated="validationResult" ref="paymentForm"></payment-form>
      <subscription-info :config="config" :pricing="pricing" :pricingValues="pricingValues"></subscription-info>
      <charge-price v-show="showPrices" :config="config" :pricing="pricing" :pricingValues="pricingValues" ref="chargePrice"></charge-price>
      <payment-agreement :config="config" ref="paymentAgreementRef"></payment-agreement>
      <p class="field-error" :class="{ 'show-error' : formError }">{{ formError }}</p>
      <submit-button v-show="showPrices" @submit="submit" :finished="finished" data-cy="btn--submit">Buy Now</submit-button>
    </div>
    <modal ref="modal" :closeable="true" labelID="portal-desc">
      <portal :key="componentKey" :type="portalType" labelID="portal-desc" @getCaptchaTokenFromPortal="setCaptchaToken"></portal>
    </modal>
  </div>
</template>

<script>
import paymentForm from '@/components/payment-form/payment-form'
import modal from '@/components/modal/modal'
import portal from '@/components/portal/portal'
import marketing from '@/services/marketing.js'
import Stripe from 'stripe'
import submitButton from '@/components/submit-button/submit-button'
import subscriptionInfo from '@/components/subscription-info/subscription-info'
import chargePrice from '@/components/charge-price/charge-price'
import errorDetails from '@/mixins/errors.js'
import paymentAgreement from '@/components/payment-agreement/payment-agreement'

export default {
  name: 'payment-charge',
  components: {
    'payment-form': paymentForm,
    'submit-button': submitButton,
    modal,
    portal,
    'charge-price': chargePrice,
    'subscription-info': subscriptionInfo,
    'payment-agreement': paymentAgreement
  },
  props: {
    pricingValues: Object,
    pricing: Object,
    config: Object,
    modal: Boolean,
    showPrices: {
      default: true,
      type: Boolean
    }
  },
  mixins: [errorDetails],
  data () {
    return {
      couponPricing: null,
      submitted: false,
      finished: false,
      formError: '',
      watcher: null,
      marketingData: marketing.paymentTracking(this.$route),
      componentKey: 0,
      captchaTimer: null
    }
  },
  created: function () {
    if (this.modal) {
      this.watcher = this.$store.watch((state, getters) => getters.isLoggedIn, () => {
        this.finishSubmit()
      })
    }
  },
  computed: {
    modalOpen: function () {
      return this.$store.getters.modalOpen
    },
    portalType: function () {
      if (this.captchaToken === null) {
        return 'captcha'
      } else {
        return this.$route.path.indexOf('upgrade') > -1 ? 'login' : 'signup'
      }
    },
    captchaToken: function () {
      return this.$store.getters.captchaToken
    },
    coupon: function () {
      return this.pricing[this.config.productName].coupon || {}
    }
  },
  methods: {
    submit: function () {
      this.$emit('attemptPurchase')
      this.formError = ''
      if (!this.$refs.chargePrice.isCompliant()) return
      if (!this.$refs.paymentAgreementRef.validateAutoRenewCheck()) return
      if (this.$store.getters.isLoggedIn || !this.modal) {
        this.$refs.paymentForm.submit()
      } else {
        this.$refs.paymentForm.isValidated()
      }
    },
    validationResult: function (valid) {
      if (valid && this.modal) {
        this.$refs.modal.openModal()
      }
    },
    finishSubmit: function () {
      this.$refs.modal.closeModal()
      this.$refs.paymentForm.submit()
    },
    paymentResponse: function (response) {
      if (response.error) {
        this.$store.dispatch('submitting', false)
        this.$store.dispatch('captchaToken', null)
        this.formError = response.error.message || 'Whoops... something went wrong with your request. Please refresh and try again.'
      } else {
        let data = {
          token: response.token.id,
          productName: this.config.productName
        }
        if (this.coupon.code) {
          data.couponCode = this.coupon.code
        }
        // hit backend endpoint with Stripe token to allow changes
        const captchaToken = this.captchaToken
        const endpoint = this.config.endpoint || '/doStripePayment'
        const config = { params: { captchaToken } }
        data = Object.assign(data, this.marketingData)
        // send confirmation of transaction to Stripe with transaction details.  User's card is charged.
        this.$http.post(this.ENV.paymentBaseUrl + endpoint, data, config).then((response) => {
          // Stripe returns a transaction confirmation with transaction details.  Push to a dataLayer for Google Tag Manager
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            'transactionId': response.data.transId,
            'transactionTotal': response.data.amountPaid,
            // 'transactionTax': 1.29,
            'transactionProducts': [{
              'sku': response.data.product,
              'name': response.data.product,
              'price': response.data.amountPaid,
              'quantity': 1
            }]
          })
          this.trackGoogleAnalyticsPurchase(response)
          this.finished = true
          this.$store.dispatch('getUserData', true)
          setTimeout(() => {
            this.$emit('chargeResponse', 'PASS')
          }, 2000)
          marketing.removeAllCookies()
          this.$store.dispatch('submitting', false)
        }).catch((error) => {
          if (error.response) {
            const errorCode = error.response.status
            if (errorCode === 401 || errorCode === 400) {
              this.formError = error.response.data.error_message
            } else {
              this.formError = this.getErrorMsg(error)
            }
          } else {
            this.formError = this.getErrorMsg(error)
          }
          this.$store.dispatch('captchaToken', null)
          this.$store.dispatch('submitting', false)
          this.forceRerender()
        })
      }
    },
    trackGoogleAnalyticsPurchase: function (response) {
      this.$gtm.trackEvent({
        event: 'purchase',
        ecommerce: {
          transaction_id: response.data.transId,
          value: response.data.amountPaid,
          currency: 'USD',
          items: [{
            'sku': response.data.product,
            'name': response.data.product,
            'price': response.data.amountPaid,
            'quantity': 1
          }]
        }
      })
    },
    setCaptchaToken: function (captchaToken) {
      this.$store.dispatch('captchaToken', captchaToken)
      // timer to clear captcha token after 10 minutes
      this.captchaTimer = setTimeout(() => {
        this.$store.dispatch('captchaToken', null)
        this.forceRerender()
      }, 600000)
      this.forceRerender() // TODO: very weird that this is needed, should figure out why.
    },
    cancelClearCaptchaToken () {
      clearTimeout(this.captchaTimer)
    },
    forceRerender: function () {
      this.componentKey += 1
    }
  },
  beforeDestroy: function () {
    // unwatch this function
    if (this.watcher) this.watcher()
    this.cancelClearCaptchaToken()
  }
}
</script>

<style scoped lang="stylus" src="./payment-charge.styl" >
